/**
 * Job List API
 *
 * API client functions for fetching the paginated job list.
 *
 * Endpoint : GET /jobs/job-list-extended
 * Method   : GET
 * Backend  : apps/backend/src/domains/works-lite/jobs/job-list-extended/routes.ts
 *
 * The extended endpoint accepts every filter the basic /jobs/job-list endpoint
 * does, plus server-side `sort_by` / `sort_order`. Sorting must be server-side
 * because the list is paginated server-side (limit/skip) — sorting only the
 * rows on the current page would be wrong.
 */

import { apiClient } from '@/lib/api';
import type {
  JobListExtendedQuery,
  JobListItem,
  JobListResponse,
  JobTabCountsResponse,
} from '@skewb-works/contracts';

export type { JobListItem, JobListResponse, JobTabCountsResponse };

/** Columns the backend allows sorting on (subset of the extended query enum). */
export type JobListSortBy = NonNullable<JobListExtendedQuery['sort_by']>;
export type JobListSortOrder = NonNullable<JobListExtendedQuery['sort_order']>;

/**
 * Job-list filters, excluding pagination.
 *
 * Kept separate from `JobListParams` rather than derived with
 * `Omit<JobListParams, 'limit' | 'skip'>`: the index signature below makes
 * `keyof` resolve to `string | number`, so `Omit` strips every named property
 * and collapses the type to the index signature alone.
 */
export interface JobListFilterParams {
  [key: string]: unknown;
  priority_id?: number[];
  job_category_id?: number;
  work_order_status_id?: number;
  work_order_status_name?: string[];
  search_term?: string;
  mir_pending_direction?: 'inbound' | 'outbound' | 'none';
  is_vulnerable?: boolean;
  sort_by?: JobListSortBy;
  sort_order?: JobListSortOrder;
}

export interface JobListParams extends JobListFilterParams {
  limit?: number;
  skip?: number;
}

/**
 * Serialise job-list filters to query params.
 *
 * Shared by the paginated list and the export download so both send byte-identical
 * filters — the export must reflect exactly what the user sees on screen.
 * Pagination (limit/skip) is handled by the callers, not here.
 */
function buildJobListSearchParams(params: JobListFilterParams): URLSearchParams {
  const searchParams = new URLSearchParams();

  if (params.priority_id?.length) searchParams.set('priority_id', params.priority_id.join(','));
  if (params.job_category_id != null)
    searchParams.set('job_category_id', String(params.job_category_id));
  if (params.work_order_status_id != null)
    searchParams.set('work_order_status_id', String(params.work_order_status_id));
  if (params.work_order_status_name != null && params.work_order_status_name.length > 0) {
    for (const name of params.work_order_status_name) {
      searchParams.append('work_order_status_name', name);
    }
  }
  if (params.search_term != null && params.search_term !== '')
    searchParams.set('search_term', params.search_term);
  if (params.mir_pending_direction != null)
    searchParams.set('mir_pending_direction', params.mir_pending_direction);
  // Only send the PSR filter when active (true). Omitting it means "all jobs".
  if (params.is_vulnerable) searchParams.set('is_vulnerable', 'true');
  // Truthiness guard (omits null/undefined AND empty string): the backend types
  // sort_by/sort_order as Zod enums, so '' fails validation (400) rather than
  // meaning "no sort". Omit it so the backend applies its default ordering.
  // A plain `!== ''` check is rejected by TS since the param type can't be ''.
  if (params.sort_by) searchParams.set('sort_by', params.sort_by);
  if (params.sort_order) searchParams.set('sort_order', params.sort_order);

  return searchParams;
}

/**
 * Fetch a paginated, optionally filtered list of jobs.
 *
 * Filter params are omitted from the request when undefined so that
 * the backend treats them as "no filter" (returns all values).
 */
export async function getJobList(params: JobListParams = {}): Promise<JobListResponse> {
  const searchParams = buildJobListSearchParams(params);
  searchParams.set('limit', String(params.limit ?? 50));
  searchParams.set('skip', String(params.skip ?? 0));

  return apiClient.get<JobListResponse>(`/jobs/job-list-extended?${searchParams.toString()}`);
}

export async function getJobTabCounts(): Promise<JobTabCountsResponse> {
  return apiClient.get<JobTabCountsResponse>('/jobs/tab-counts');
}

/**
 * Download the full filtered job list as a CSV or Excel file.
 *
 * The file is generated server-side (GET /jobs/job-list-export) so the export
 * reflects the complete filtered result set without pulling every row into the
 * browser, and so any field-level permission rules are enforced on the server.
 * Filters use the same query params as the list itself — pagination is omitted
 * because the endpoint always exports everything that matches.
 */
export async function downloadJobListExport(
  params: JobListFilterParams = {},
  format: 'csv' | 'xlsx' = 'csv',
  fields?: string[],
): Promise<Blob> {
  const searchParams = buildJobListSearchParams(params);
  searchParams.set('format', format);
  // Omitted entirely when no selection is made, so the server applies its
  // default column set rather than us duplicating that default here.
  if (fields?.length) searchParams.set('fields', fields.join(','));

  return apiClient.getBlob(`/jobs/job-list-export?${searchParams.toString()}`);
}

export interface JobExportField {
  key: string;
  label: string;
  default: boolean;
}

/**
 * Fetch the columns available for export.
 *
 * Driven by the server's column registry — the same source the export
 * validates against — so the picker can never offer a field the export would
 * reject, and the two cannot drift apart.
 */
export async function getJobExportFields(): Promise<JobExportField[]> {
  const response = await apiClient.get<{ fields: JobExportField[] }>(
    '/jobs/job-list-export/fields',
  );
  return response.fields;
}
